import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

function money(v, currency) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'GBP' }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency || ''}`.trim();
  }
}

export default function PriceBook() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    unit_price: 0,
    unit_cost: '',
    duration_minutes: '',
    currency: 'GBP',
    bookable_online: true,
  });
  const [togglingId, setTogglingId] = useState(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => {
      const a = String(it.name || '').toLowerCase();
      const b = String(it.description || '').toLowerCase();
      return a.includes(s) || b.includes(s);
    });
  }, [items, q]);

  async function fetchItems() {
    setError('');
    setLoading(true);
    try {
      const res = await apiService.get('price-book?active=1');
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e.message || 'Failed to load price book');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreate() {
    setError('');
    setBusy(true);
    try {
      const dm = String(form.duration_minutes ?? '').trim();
      await apiService.post('price-book', {
        name: form.name,
        description: form.description || null,
        unit_price: Number(form.unit_price || 0),
        ...(form.unit_cost !== '' && form.unit_cost != null ? { unit_cost: Number(form.unit_cost) } : {}),
        ...(dm !== '' && Number(dm) >= 0 ? { duration_minutes: Number(dm) } : {}),
        currency: form.currency || 'GBP',
        active: true,
        bookable_online: !!form.bookable_online,
      });
      setOpen(false);
      setForm({
        name: '',
        description: '',
        unit_price: 0,
        duration_minutes: '',
        currency: 'GBP',
        bookable_online: true,
      });
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Failed to create item');
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleBookableOnline(item, checked) {
    setError('');
    setTogglingId(item.id);
    try {
      const updated = await apiService.put(`price-book/${item.id}`, {
        bookable_online: checked,
      });
      setItems((prev) =>
        prev.map((row) => (row.id === item.id ? { ...row, ...updated } : row))
      );
    } catch (e) {
      setError(e.message || 'Failed to update online booking setting');
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDelete(id) {
    setError('');
    try {
      await apiService.delete(`price-book/${id}`);
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Failed to delete item');
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Price book
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
            Toggle &quot;Online booking&quot; to show or hide a service on the public booking page and waste calculator tiers.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ textTransform: 'none' }}>
          Add item
        </Button>
      </Box>

      <TextField
        size="small"
        placeholder="Search…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ mb: 2, maxWidth: 420, width: '100%' }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filtered.length === 0 ? (
            <Card>
              <CardContent>
                <Typography sx={{ fontWeight: 800 }}>No items.</Typography>
                <Typography variant="body2" color="text.secondary">Add your most common services here.</Typography>
              </CardContent>
            </Card>
          ) : (
            filtered.map((it) => (
              <Card key={it.id} sx={{ boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }}>{it.name}</Typography>
                    {it.description && (
                      <Typography variant="body2" color="text.secondary">
                        {it.description}
                      </Typography>
                    )}
                    {it.duration_minutes != null && Number(it.duration_minutes) > 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Est. time: {it.duration_minutes} min
                      </Typography>
                    ) : null}
                    {it.bookable_online ? (
                      <Chip label="On public booking" size="small" color="primary" variant="outlined" sx={{ mt: 1 }} />
                    ) : null}
                  </Box>
                  <FormControlLabel
                    sx={{ mr: 0, whiteSpace: 'nowrap' }}
                    control={
                      <Switch
                        checked={!!it.bookable_online}
                        onChange={(e) => handleToggleBookableOnline(it, e.target.checked)}
                        disabled={togglingId === it.id}
                        color="primary"
                      />
                    }
                    label="Online booking"
                  />
                  <Typography sx={{ fontWeight: 900, whiteSpace: 'nowrap' }}>
                    {money(it.unit_price, it.currency)}
                  </Typography>
                  <IconButton aria-label="Delete" onClick={() => handleDelete(it.id)}>
                    <DeleteIcon />
                  </IconButton>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Add price book item</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Unit price (sell)"
                type="number"
                value={form.unit_price}
                onChange={(e) => setForm((f) => ({ ...f, unit_price: e.target.value }))}
                fullWidth
                inputProps={{ step: '0.01', min: 0 }}
              />
              <TextField
                label="Unit cost (internal)"
                type="number"
                value={form.unit_cost}
                onChange={(e) => setForm((f) => ({ ...f, unit_cost: e.target.value }))}
                fullWidth
                inputProps={{ step: '0.01', min: 0 }}
                helperText="Optional — for job margin / materials"
              />
              <TextField
                label="Est. duration (minutes)"
                type="number"
                value={form.duration_minutes}
                onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))}
                fullWidth
                inputProps={{ step: '1', min: 0 }}
                helperText="Optional — for scheduling / quotes"
              />
              <TextField
                label="Currency"
                value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                fullWidth
                sx={{ gridColumn: { sm: '1 / -1' } }}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.bookable_online}
                  onChange={(e) => setForm((f) => ({ ...f, bookable_online: e.target.checked }))}
                  color="primary"
                />
              }
              label="Offer on public online booking"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={busy || !form.name.trim()} sx={{ textTransform: 'none' }}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

